import React from 'react';
import { LucideIcon } from 'lucide-react';
import { cn } from '@/utils/cn';
import AppTooltip from '@/components/ui/_refactor/popup/tooltip/app-tooltip/app-tooltip';

export type TableActionButtonVariant = 'edit' | 'delete' | 'disable' | 'neutral' | 'custom';

export interface TableActionButtonProps extends React.ButtonHTMLAttributes<HTMLButtonElement> {
  icon: LucideIcon;
  onClick?: () => void;
  disabled?: boolean;
  title?: string;
  variant?: TableActionButtonVariant;
  iconColor?: string;
  bgColor?: string;
  hoverColor?: string;
  disabledIconColor?: string;
  className?: string;
  tooltip?: React.ReactNode;
  tooltipClassName?: string;
}

const variantStyles: Record<
  TableActionButtonVariant,
  {
    bgColor: string;
    hoverColor: string;
    iconColor: string;
  }
> = {
  edit: {
    bgColor: 'bg-[#DEF7EC]',
    hoverColor: 'hover:bg-[#C4F0D8]',
    iconColor: 'text-[#0B8A00]',
  },
  delete: {
    bgColor: 'bg-[#FFE1D0]',
    hoverColor: 'hover:bg-[#FFD4B8]',
    iconColor: 'text-[#CC0202]',
  },
  disable: {
    bgColor: 'bg-[#F5F5F5]',
    hoverColor: 'hover:bg-[#E5E5E5]',
    iconColor: 'text-[#9D9D9C]',
  },
  neutral: {
    bgColor: 'bg-[#F5F5F5]',
    hoverColor: 'hover:bg-[#E5E5E5]',
    iconColor: 'text-[#0C8DBF]',
  },
  custom: {
    bgColor: '',
    hoverColor: '',
    iconColor: '',
  },
};

const TableActionButton = ({
  icon: Icon,
  onClick,
  disabled = false,
  title,
  variant = 'custom',
  iconColor,
  bgColor,
  hoverColor,
  disabledIconColor = 'text-[#9D9D9C]',
  className,
  tooltip,
  tooltipClassName,
  ...rest
}: TableActionButtonProps) => {
  const styles = variantStyles[variant];
  const finalBgColor = bgColor || styles.bgColor;
  const finalHoverColor = hoverColor || styles.hoverColor;
  const finalIconColor = iconColor || styles.iconColor;

  const button = (
    <button
      disabled={disabled}
      className={cn(
        'w-[24px] h-[24px] p-1 rounded-sm justify-center items-center flex transition-colors group',
        'disabled:bg-[#F5F5F5] disabled:cursor-not-allowed cursor-pointer',
        finalBgColor,
        !disabled && finalHoverColor,
        className
      )}
      onClick={onClick}
      title={tooltip ? undefined : title}
      {...rest}
    >
      <Icon className={cn('w-[16px] h-[16px] flex-shrink-0', disabled ? disabledIconColor : finalIconColor)} />
    </button>
  );

  if (tooltip) {
    return (
      <AppTooltip content={tooltip} contentClassName={cn('z-[1001]', tooltipClassName)}>
        {button}
      </AppTooltip>
    );
  }

  return button;
};

export default TableActionButton;
